import React, { useState } from 'react';
import classNames from 'classnames';
import { observer } from 'src/utils/mobx-react';
import { action, computed, observable, makeAutoObservable } from 'mobx';

class State {
    @observable public page: number = 0;

    @observable public isCSVInProgress: boolean = false;
}

class PaginationState {
    private readonly defaultPerPage = 10;
    public constructor(
        private readonly props: PaginationPropsType,
        private readonly state: State
    ) {
        makeAutoObservable(this);
    }

    @computed
    public get isCsvButtonDisabled(): boolean {
        return this.state.isCSVInProgress === true || this.props.total === 0;
    }
    @computed public get pages(): number {
        const perPage = this.props.perPage ?? this.defaultPerPage;
        return this.props.total === 0 && this.props.perPage === 0 ? 0 : Math.ceil(this.props.total / perPage);
    }

    @computed public get isLastActive(): boolean {
        const perPage = this.props.perPage ?? this.defaultPerPage;
        return this.pages > 0 ? this.state.page === this.pages - 1 : (this.props.currentVisibleItems ?? 0) < perPage;
    }

    @action public generateCSV = (): void => {
        this.state.isCSVInProgress = true;
        if (this.props.onGenerateCSV !== undefined) {
            this.props.onGenerateCSV(this.changeCSVInProgressStatus);
        }
    };

    @action public changeCSVInProgressStatus = (): void => {
        this.state.isCSVInProgress = false;
    };

    @action public addPage = (shift: number): void => {
        const { total, currentVisibleItems } = this.props;
        const perPage = this.props.perPage ?? this.defaultPerPage;
        const page = this.state.page;
        const pages = total === 0 && perPage === 0 ? 0 : Math.ceil(total / perPage);
        const isLast = pages > 0 ? page > pages : (currentVisibleItems ?? 0) < perPage;

        if (shift < 0 ? page <= 0 : isLast) {
            return;
        }

        this.setPage(page + shift);
    };

    @action public setPage = (page: number): void => {
        const { onPageChange } = this.props;

        onPageChange(page + 1);
        this.state.page = page;
    };

    @action public setPrevPage = (): void => {
        this.addPage(-1);
    };

    @action public setNextPage = (): void => {
        this.addPage(1);
    };
}

type PaginationPropsType = {
    className: string;
    page: number;
    total: number;
    onPageChange: (page: number) => void;
    perPage?: number;
    currentVisibleItems?: number;
    visible?: number;
    onGenerateCSV?: (callback: () => void) => void;
    nextPageLabel?: string;
    previousPageLabel?: string;
    generateCSVLabel?: string;
};

export const Pagination = observer('Pagination', (props: PaginationPropsType) => {
    const {
        nextPageLabel = 'Next Page',
        previousPageLabel = 'Previous Page',
        generateCSVLabel = 'Export to CSV',
        visible = 10,
    } = props;

    const [state] = useState(() => new State());
    const paginationState = new PaginationState(props, state);

    let omit = 0;
    let shift = 0;

    const CsvButton = (
        <li
            key='page-previous'
            className='pagination__item'
        >
            <button
                data-test='export-to-csv'
                onClick={paginationState.generateCSV}
                disabled={paginationState.isCsvButtonDisabled}
                className='btn'
            >
                {generateCSVLabel}
            </button>
        </li>
    );

    const areAllVisible = visible >= paginationState.pages;

    if (!areAllVisible) {
        omit = visible / 2;
        if (omit > state.page) {
            shift = omit - state.page;
        } else if (omit > paginationState.pages - state.page) {
            shift = omit - (paginationState.pages - state.page);
        }
    }

    const className = classNames([props.className, 'pagination']);

    const buildPaginationList = (): Array<JSX.Element> => {
        const elementsOut: Array<JSX.Element> = [];

        for (let i = 0; i < paginationState.pages; i++) {
            const pageClassName = classNames([
                'pagination__item',
                {
                    'pagination__item--active': i === state.page,
                },
            ]);
            const isBoundary = i === 0 || i === paginationState.pages - 1;
            const isInVisibleInterval = i > state.page - (omit + shift) + 1 && i < state.page + (omit + shift);

            if (!areAllVisible && !isBoundary && !isInVisibleInterval) {
                if (i === 1 || i === paginationState.pages - 2) {
                    elementsOut.push(
                        <li
                            key={`page-divider-${state.page}${i}`}
                            className='pagination__divider'
                        >
                            <span>...</span>
                        </li>
                    );
                }
                continue;
            }

            elementsOut.push(
                <li
                    key={`page-${i}`}
                    className={pageClassName}
                >
                    <button
                        data-test={`pagination-button-number-${i + 1}`}
                        onClick={() => paginationState.setPage(i)}
                        className='btn'
                    >
                        {i + 1}
                    </button>
                </li>
            );
        }

        return elementsOut;
    };

    return (
        <ul className={className}>
            {props.onGenerateCSV === undefined ? null : CsvButton}
            {
                <li
                    key='page-previous-'
                    className='pagination__item'
                >
                    <button
                        onClick={paginationState.setPrevPage}
                        disabled={state.page === 0}
                        className='btn'
                        data-test='pagination-button-previous-page'
                    >
                        {previousPageLabel}
                    </button>
                </li>
            }
            {buildPaginationList()}
            {
                <li
                    key='page-next'
                    className='pagination__item'
                >
                    <button
                        className='btn'
                        disabled={paginationState.isLastActive}
                        onClick={paginationState.setNextPage}
                        data-test='pagination-button-next-page'
                    >
                        {nextPageLabel}
                    </button>
                </li>
            }
        </ul>
    );
});
